import React, { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { alpha, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { UserRole } from '@kotipizzagroup/kotipizza-users-api-client';
import { Commerce } from '@kotipizzagroup/kotipizza-shared-types';
import {
  CAMPAIGNS_WRITE,
  LOYALTY_WRITE,
  PRODUCTS_WRITE,
  SHOPS_WRITE,
  usePermission,
} from '../../shared/utils/permissions';
import { useUserPermissions } from '../../apis/user-details';
import useRestaurantStore from '../../stores/restaurantStore';
import { useAllIngredients, useAllProductMenus, useAllProducts, useDefaultProductMenu } from '../../apis/products-api';
import {
  dedupeByWarehouseItemId,
  isIngredientSelectable,
  isProductSelectable,
} from '../../shared/utils/outOfStockFilters';
import { formatReturnDateOption, OutOfStockItem, OutOfStockItemType } from './OutOfStockUtils';

/** Number of upcoming days offered as return dates when marking an item out of stock. */
export const SCHEDULE_LENGTH = 7;

/** The ingredient/product categories that can be marked out of stock at all. */
export const VISIBLE_INGREDIENT_CATEGORIES = [
  Commerce.IngredientCategory.BASE_SAUCE,
  Commerce.IngredientCategory.CHEESE,
  Commerce.IngredientCategory.DOUGH,
  Commerce.IngredientCategory.INGREDIENT,
  Commerce.IngredientCategory.SAUCE,
  Commerce.IngredientCategory.SPICE,
  Commerce.IngredientCategory.DIP,
];
export const VISIBLE_PRODUCT_CATEGORIES = [
  Commerce.ProductCategory.ALCOHOL,
  Commerce.ProductCategory.DIP,
  Commerce.ProductCategory.DIPATTAVAT,
  Commerce.ProductCategory.DRINK,
  Commerce.ProductCategory.ICECREAM,
];

/** The small uppercase label used for field labels, column headers and the rows' mobile state label. */
export const MetaLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export type ScheduleDateOption = {
  value: string;
  label: string;
  duration: number;
};

/**
 * Provides the day-relative return-date options shared by the add/modify flows.
 * `today` is captured once (start of day) so the options stay stable across renders.
 */
export const useScheduleDates = (): { today: dayjs.Dayjs; dateOptions: ScheduleDateOption[] } => {
  const [today] = useState(() => dayjs().startOf('day'));

  const dateOptions = useMemo<ScheduleDateOption[]>(
    () =>
      Array.from({ length: SCHEDULE_LENGTH }, (_, offset) => {
        const date = today.add(offset + 1, 'days');
        return {
          label: formatReturnDateOption(date),
          value: date.format(),
          duration: offset + 1,
        };
      }),
    [today]
  );

  return { today, dateOptions };
};

/** Resolves the restaurant's external shop id from the route param, falling back to the store. */
export const useExternalShopId = (): string => {
  const { selectedShopExternalId } = useRestaurantStore();
  const { restaurant } = useParams<{ restaurant: string }>();
  // The URL wins: the store can still hold the previously viewed shop on the first render of another's page.
  return restaurant ?? selectedShopExternalId ?? '';
};

/**
 * Resolves what a restaurant is allowed to mark out of stock, so the add-picker only offers items the
 * restaurant actually sells:
 *  - products = the productIds of the menus assigned to this shop, plus the global default menu, and
 *  - ingredients = the ingredientIds used in the recipes of those products.
 *
 * Fails open by design: callers should skip filtering for a dimension whose set is empty (shop has no
 * menus, or product recipes are not populated by the bulk products endpoint) rather than hide everything.
 */
const useRestaurantMenuAvailability = (
  externalShopId: string
): {
  availableProductIds: Set<number>;
  availableIngredientIds: Set<number>;
  isLoading: boolean;
  isError: boolean;
} => {
  // isInitialLoading, not isLoading: a query that is disabled or already has cached data must not read
  // as loading, or callers spin forever waiting for a fetch that never starts.
  const { data: allMenus, isInitialLoading: isMenusLoading, isError: isMenusError } = useAllProductMenus();
  const {
    data: defaultMenu,
    isInitialLoading: isDefaultMenuLoading,
    isError: isDefaultMenuError,
  } = useDefaultProductMenu();
  const { data: products, isInitialLoading: isProductsLoading, isError: isProductsError } = useAllProducts(false);

  const availableProductIds = useMemo(() => {
    const ids = new Set<number>();
    allMenus
      ?.filter((menu) => menu.shopsHavingMenu?.some((shop) => shop.shopExternalId === externalShopId))
      .forEach((menu) => menu.productIds?.forEach((id) => ids.add(id)));
    defaultMenu?.productIds?.forEach((id) => ids.add(id));
    return ids;
  }, [allMenus, defaultMenu, externalShopId]);

  const availableIngredientIds = useMemo(() => {
    const ids = new Set<number>();
    products
      ?.filter((product) => product.productId != null && availableProductIds.has(product.productId))
      .forEach((product) => product.ingredients?.forEach((recipeItem) => ids.add(recipeItem.ingredientId)));
    return ids;
  }, [products, availableProductIds]);

  return {
    availableProductIds,
    availableIngredientIds,
    isLoading: isMenusLoading || isDefaultMenuLoading || isProductsLoading,
    isError: isMenusError || isDefaultMenuError || isProductsError,
  };
};

/** An ingredient or product that can still be picked in the add flow. */
export type OutOfStockCandidate = {
  id: number;
  type: OutOfStockItemType;
  name: string;
};

/**
 * Everything this restaurant may still mark out of stock: sellable master records from the visible
 * categories, deduped by warehouse item, narrowed to what the restaurant's menus contain, minus the
 * items that are out of stock already. `isLoading` and `isError` cover every query behind the list —
 * the catalogs as well as the menu lookup — so callers can tell "still loading" and "could not load"
 * apart from "genuinely no matches".
 */
export const useOutOfStockCandidates = (
  existingOutOfStockItems: OutOfStockItem[]
): { candidates: OutOfStockCandidate[]; isLoading: boolean; isError: boolean } => {
  const externalShopId = useExternalShopId();
  const {
    availableProductIds,
    availableIngredientIds,
    isLoading: isAvailabilityLoading,
    isError: isAvailabilityError,
  } = useRestaurantMenuAvailability(externalShopId);
  const {
    data: ingredients,
    isInitialLoading: isIngredientsLoading,
    isError: isIngredientsError,
  } = useAllIngredients(false);
  const { data: products, isInitialLoading: isProductsLoading, isError: isProductsError } = useAllProducts(false);

  const candidates = useMemo(() => {
    const ingredientCandidates: OutOfStockCandidate[] = dedupeByWarehouseItemId(
      ingredients
        ?.filter((i) => i.ingredientCategoryId && VISIBLE_INGREDIENT_CATEGORIES.includes(i.ingredientCategoryId))
        .filter(isIngredientSelectable) ?? []
    ).map((i) => ({ id: Number(i.ingredientId), type: 'ingredient' as const, name: i.name ?? '' }));

    const productCandidates: OutOfStockCandidate[] =
      products
        ?.filter((p) => p.productCategoryId && VISIBLE_PRODUCT_CATEGORIES.includes(p.productCategoryId))
        .filter(isProductSelectable)
        .map((p) => ({ id: Number(p.productId), type: 'product' as const, name: p.name ?? '' })) ?? [];

    // Restrict to what this restaurant sells. Fail open per dimension: if a set is empty (shop has no
    // menus, or product recipes are not populated), skip that filter rather than hide everything.
    const availableIngredients =
      availableIngredientIds.size > 0
        ? ingredientCandidates.filter((candidate) => availableIngredientIds.has(candidate.id))
        : ingredientCandidates;
    const availableProducts =
      availableProductIds.size > 0
        ? productCandidates.filter((candidate) => availableProductIds.has(candidate.id))
        : productCandidates;

    return [...availableIngredients, ...availableProducts].filter(
      (candidate) =>
        !existingOutOfStockItems.some(
          (item) => item.type === candidate.type && item.groupedItemIds.includes(candidate.id)
        )
    );
  }, [existingOutOfStockItems, ingredients, products, availableIngredientIds, availableProductIds]);

  return {
    candidates,
    isLoading: isAvailabilityLoading || isIngredientsLoading || isProductsLoading,
    isError: isAvailabilityError || isIngredientsError || isProductsError,
  };
};

/**
 * Out-of-stock view/update permissions (admin write scopes imply both). The roles are query-backed, so
 * `isLoading` lets callers wait rather than render a "no access" blank while they are still arriving.
 */
export const useOutOfStockPermissions = (): { canView: boolean; canUpdate: boolean; isLoading: boolean } => {
  const { isLoading } = useUserPermissions();
  const isAdminUser = usePermission([PRODUCTS_WRITE, LOYALTY_WRITE, SHOPS_WRITE, CAMPAIGNS_WRITE]);
  const canView = usePermission([UserRole.OutOfStock_CanView]);
  const canUpdate = usePermission([UserRole.OutOfStock_CanUpdate]);
  return {
    canView: canView || isAdminUser,
    canUpdate: canUpdate || isAdminUser,
    isLoading,
  };
};

/** The "to be returned on" picker shared by the add/edit dialogs. */
export const ScheduleDateField: React.FC<{
  value: string;
  options: ScheduleDateOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ value, options, onChange, disabled }) => {
  const { t } = useTranslation();
  // Generated rather than constant: both dialogs render this field.
  const labelId = useId();

  return (
    <div>
      <FieldLabel id={labelId}>{t('To be returned on')}</FieldLabel>
      <DateControl>
        <Select
          value={value}
          onChange={(event: SelectChangeEvent) => onChange(event.target.value)}
          disabled={disabled}
          labelId={labelId}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {/* Parentheses, not a hyphen: "to 20.8. - 1 päivä" reads as a date range. */}
              {option.label} ({option.duration} {option.duration === 1 ? t('day') : t('days')})
            </MenuItem>
          ))}
        </Select>
      </DateControl>
    </div>
  );
};

const FieldLabel = styled(MetaLabel)`
  display: block;
  margin-bottom: 8px;
`;

// A seven-option date picker should not stretch across a desktop dialog, but on a full-screen phone
// dialog it lines up with the other controls.
const DateControl = styled(FormControl)`
  && {
    width: 100%;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      max-width: 320px;
    }
  }
`;

/**
 * Neutral "raaka-aine"/"tuote" marker shown next to an item's name. Its own keys rather than the
 * generic `ingredient` / `product` ones, which product menus and the admin list also use and word
 * differently. Both keys are spelled out in literal `t()` calls because that is the only form
 * i18next-scanner extracts. Uppercasing is left to CSS, which also tidies the `en` fallback.
 */
export const ItemTypeChip: React.FC<{ type: OutOfStockItemType }> = ({ type }) => {
  const { t } = useTranslation();
  return <TypeChip>{type === 'ingredient' ? t('outOfStock.type.ingredient') : t('outOfStock.type.product')}</TypeChip>;
};

const TypeChip = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.secondary};
  background-color: ${({ theme }) => alpha(theme.palette.text.primary, 0.08)};
`;

/** Explanatory info box shown in the add flow. */
export const OutOfStockInfoBox: React.FC = () => {
  const { t } = useTranslation();
  return (
    <InfoBoxContainer>
      <InfoIcon />
      <p>
        {t(
          'Removing ingredients will also remove all products from the webshop which have the ingredient in their recipe'
        )}
        <br />
        <br />
        {t('Example: removing ham will remove Americana, Opera Special, Lihamestari etc.')}
      </p>
    </InfoBoxContainer>
  );
};

const InfoBoxContainer = styled.div`
  display: flex;
  gap: 12px;
  background: #eaf5fb;
  padding: 16px;
  border-radius: 8px;
  color: #1f4e79;
`;

// The "i" is drawn in CSS: it is an icon glyph, not copy to be translated.
const InfoIcon = styled.div`
  background: #1f4e79;
  color: ${({ theme }) => theme.palette.common.white};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  min-width: 24px;
  display: flex;
  margin-top: 12px;
  align-items: center;
  justify-content: center;
  font-weight: bold;

  &::before {
    content: 'i';
  }
`;
